import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { RootState } from 'js/src/libs/RootState';

export enum ClipCreatorPage {
    Setup = 'setup',
    Upload = 'upload',
    Review = 'review',
}

type ClipCreatorPageData = {
    title: string;
    icon: string;
    selected: boolean;
    disabled: boolean;
    disabledReason?: string;
};

type InitialState = {
    allIds: ClipCreatorPage[];
    byId: Record<ClipCreatorPage, ClipCreatorPageData>;
    selected: ClipCreatorPage;
};

const initialState: InitialState = {
    selected: ClipCreatorPage.Setup,
    allIds: [ClipCreatorPage.Setup, ClipCreatorPage.Upload, ClipCreatorPage.Review],
    byId: {
        [ClipCreatorPage.Setup]: {
            title: 'Setup',
            icon: 'settings',
            selected: false,
            disabled: true,
        },
        [ClipCreatorPage.Upload]: {
            title: 'Upload content',
            icon: 'add',
            selected: false,
            disabled: true,
        },
        [ClipCreatorPage.Review]: {
            title: 'Review clips',
            icon: 'subscriptions',
            selected: false,
            disabled: true,
        },
    },
};

export const pages = createSlice({
    name: 'pages',
    initialState: initialState,
    reducers: {
        setSelectedPage: (state, action: PayloadAction<ClipCreatorPage>) => {
            const step = action.payload;
            state.selected = step;
            state.allIds.forEach((id) => {
                state.byId[id].selected = step === id;
            });
        },
        disablePage: (state, action: PayloadAction<{ id: ClipCreatorPage; reason?: string }>) => {
            const { id, reason } = action.payload;
            state.byId[id].disabled = true;
            state.byId[id].disabledReason = reason;
        },
        enablePage: (state, action: PayloadAction<{ id: ClipCreatorPage }>) => {
            const { id } = action.payload;
            state.byId[id].disabled = false;
            state.byId[id].disabledReason = undefined;
        },
        disableAllPages: (state) => {
            state.allIds.forEach((id) => {
                state.byId[id].disabled = true;
            });
        },
    },
    selectors: {
        getSelectedPage: (state) => state.selected,
        getPageById: (state, id: ClipCreatorPage) => state.byId[id],
    },
});

export const { setSelectedPage, enablePage, disablePage, disableAllPages } = pages.actions;
export const { getSelectedPage, getPageById } = pages.getSelectors(
    (state: RootState) => state.clip_creator.pages,
);
